import type { Cadence } from './reportEnums.constants';
import { parseMeasurementPeriodEndYearMo } from './reportMeasurementPeriod.util';
import {
  clampYearMoToLastCompleted,
  reportingPeriodToYearMo,
} from './reportYearMo.util';

/**
 * Resolves the warehouse `YYYYMM` anchor for monthly/quarterly BigQuery sections.
 * @param cadence - Report cadence.
 * @param reportingPeriod - Stored reporting period label.
 * @param measurementPeriod - Optional JOC measurement period on the report.
 * @returns Six-digit year-month or null when not resolvable.
 */
export const resolveReportWarehouseYearMo = (
  cadence: Cadence,
  reportingPeriod: string,
  measurementPeriod?: string | null,
): string | null => {
  if (cadence === 'adhoc') {
    const source = measurementPeriod?.trim() || reportingPeriod.trim();
    return parseMeasurementPeriodEndYearMo(source);
  }
  return reportingPeriodToYearMo(reportingPeriod, cadence);
};

/**
 * Resolves the `YYYYMM` used to load monthly warehouse bundles.
 * Period-end months that are still in progress or in the future are replaced
 * with the last completed calendar month so hero KPIs are not empty.
 * @param cadence - Report cadence.
 * @param reportingPeriod - Stored reporting period label.
 * @param measurementPeriod - Optional JOC measurement period on the report.
 * @param asOf - Instant to evaluate (defaults to now, UTC).
 * @returns Six-digit year-month or null when not resolvable.
 */
export const resolveMonthlyBundleWarehouseYearMo = (
  cadence: Cadence,
  reportingPeriod: string,
  measurementPeriod?: string | null,
  asOf: Date = new Date(),
): string | null => {
  const yearMo = resolveReportWarehouseYearMo(
    cadence,
    reportingPeriod,
    measurementPeriod,
  );
  if (!yearMo) {
    return null;
  }
  return clampYearMoToLastCompleted(yearMo, asOf);
};

/**
 * Picks a warehouse measurement period whose end month matches `yearMo`.
 * @param yearMo - Anchor `YYYYMM`.
 * @param options - Distinct measurement period values for the customer.
 * @returns Matching period value or null.
 */
export const findMeasurementPeriodForYearMo = (
  yearMo: string,
  options: string[],
): string | null => {
  for (const option of options) {
    if (parseMeasurementPeriodEndYearMo(option) === yearMo) {
      return option;
    }
  }
  return null;
};
